// src/pages/UpgradePlan.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Minus,
  Plus,
  Users,
  Award,
  Zap,
  AlertTriangle,
  ArrowRight,
  Package,
  Loader2,
  Calendar,
  CheckCircle,
  Shield,
  StepBack,
  ArrowLeft,
} from "lucide-react";
import {
  calculatePrice,
  createFreeTrialToPaidSubscription,
  createUpgradeSubscription,
  confirmUpgrade,
  confirmSeatAddOn,
  cancelPendingUpgrade,
  getSubscriptionStatus,
  getPurchaseHistory,
  getUpgradeOptions,
  renewSubscription,
  confirmPayment,
  retryPurchase,
} from "@/services/subscriptionService";
import { getEnabledFeatures } from "@/constants/planFeatures";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { requestPlanUpgrade, getMySeatRequest } from "@/services/adminService";
import BuySeatsDialog from "@/components/admin/BuySeatsDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/* ---------------------------------------------
   Helpers
--------------------------------------------- */
const formatCurrency = (amount) =>
  `₹${Math.max(0, amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PageShell = ({
  title,
  subtitle,
  children,
  isSubscriptionCancelled,
  onBack,
}) => (
  // Matches the standard admin-page container: full width inside the layout's
  // padded <main>, a simple vertical rhythm — no self-imposed bg / max-width /
  // extra padding (those made this page inconsistent with the rest of the app).
  <div className="space-y-6">
    <header>
      <div className="flex gap-3 items-center">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          {title}
        </h1>
      </div>
      {subtitle && (
        <p className="mt-2 ml-12 max-w-2xl text-sm text-muted-foreground">
          {subtitle}
        </p>
      )}
    </header>

    <div className="space-y-6">{children}</div>
  </div>
);

/* ---------------------------------------------
   Step Header
--------------------------------------------- */
const StepHeader = ({ step, title, subtitle }) => (
  <div className="mb-5">
    <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold tracking-wide mb-2">
      STEP {step}
    </div>
    <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    {subtitle && (
      <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
    )}
  </div>
);

/* Compact label/value used in the Step-1 recap bar. */
const SummaryItem = ({ label, value, accent }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className={`text-sm font-semibold truncate ${accent ? "text-primary" : "text-foreground"}`}>
      {value}
    </p>
  </div>
);

/* ---------------------------------------------
   Header Banner (reason-based)
--------------------------------------------- */
const ReasonBanner = ({ reason, currentPlan }) => {
  if (!currentPlan) return null;

  const themes = {
    renewal: {
      bg: "from-indigo-600 to-indigo-700",
      icon: <Calendar className="w-5 h-5" />,
      title: "Subscription ended — renew to continue",
      text: `Your previous subscription (${currentPlan.plan_name}) ended. Renew to restore access.`,
    },
    need_to_purchase_seats: {
      bg: "from-emerald-600 to-emerald-700",
      icon: <Users className="w-5 h-5" />,
      title: "You need more seats",
      text: "Add seats or move to a higher plan.",
    },
    plan_limit_exceeded: {
      bg: "from-amber-600 to-amber-700",
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "Plan seat limit reached",
      text: "Upgrade to continue adding employees.",
    },
    feature_upgrade_required: {
      bg: "from-rose-600 to-rose-700",
      icon: <Zap className="w-5 h-5" />,
      title: "Upgrade required",
      text: "This feature is available on higher plans only.",
    },
  };

  const cfg = themes[reason] ?? themes.need_to_purchase_seats;

  return (
    <div
      className={`rounded-2xl p-5 text-white bg-gradient-to-r ${cfg.bg} shadow-lg`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{cfg.icon}</div>
        <div>
          <h3 className="font-semibold">{cfg.title}</h3>
          <p className="text-sm mt-1 opacity-90">{cfg.text}</p>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------
   Plan Card
--------------------------------------------- */
const PlanCard = ({
  plan,
  isCurrent,
  isSelected,
  onSelect,
  isUpgradeMandatory,
  isDowngrade,
  isRenewal,
  exceedsLimit,
  billingCycle,
  allPlans,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const price =
    billingCycle === 'annual'
      ? plan.price_per_user_annual || 0
      : plan.price_per_user_monthly || 0;

  const formattedPrice = price === 0 ? 'Free' : `₹${price.toFixed(0)}`;

  let cardClasses = 'p-4 rounded-2xl transition-all duration-200 border flex flex-col h-full bg-card relative';
  let buttonClasses = 'mt-4 inline-flex items-center justify-center w-full text-xs font-semibold rounded-xl py-2.5 transition';

  // During renewal, allow downgrades. Otherwise, block them.
  const isDisabled =
    (isDowngrade && !isRenewal) || (isCurrent && isUpgradeMandatory) || exceedsLimit;

  if (isDisabled) {
    // Keep the amber “too small” visual, but make it non-selectable
    if (exceedsLimit) {
      cardClasses +=
        " opacity-70 cursor-not-allowed border-amber-500 bg-amber-50 dark:bg-amber-900/10 shadow-sm";
      buttonClasses += " bg-amber-500 text-white cursor-not-allowed";
    } else {
      cardClasses +=
        " opacity-60 cursor-not-allowed bg-muted border-border";
      buttonClasses +=
        " bg-muted text-muted-foreground cursor-not-allowed";
    }
  } else if (isCurrent) {
    cardClasses += " border-primary bg-primary/5 shadow-sm";
    buttonClasses += " bg-primary text-primary-foreground cursor-default";
  } else if (isSelected) {
    cardClasses += " border-primary bg-primary/10 ring-2 ring-primary/30 shadow-sm";
    buttonClasses += " bg-primary text-primary-foreground hover:bg-primary/90";
  } else {
    cardClasses +=
      " border-border hover:border-primary/50 cursor-pointer hover:shadow-md";
    buttonClasses +=
      " border border-primary text-primary hover:bg-primary hover:text-primary-foreground";
  }

  const handleClick = () => {
    if (isDisabled) return;
    onSelect(plan.plan_id);
  };

  // Inheritance
  const previousPlan = allPlans.find(
    (p) => p.display_order === plan.display_order - 1
  );

  const prevKeys = previousPlan
    ? getEnabledFeatures(previousPlan.features || {}).map((f) => f.key)
    : [];

  const currentFeatures = getEnabledFeatures(plan.features || {});
  const newFeatures = previousPlan
    ? currentFeatures.filter((f) => !prevKeys.includes(f.key))
    : currentFeatures;

  const hasNewFeatures = newFeatures.length > 0;

  const isUnlimited = plan.features?.employee_limit === -1;
  const seatText = isUnlimited
    ? 'Unlimited'
    : plan.features?.employee_limit || '0';

  return (
    <div className={cardClasses} onClick={handleClick}>
      {/* Popular badge */}
      {plan.is_popular && (
        <div className="absolute -top-2 right-3 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full font-semibold shadow-sm">
          MOST POPULAR
        </div>
      )}

      {/* Annual — 2 months free ribbon */}
      {billingCycle === 'annual' && price > 0 && (
        <div className="absolute -top-3 left-3 bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold shadow-sm">
          Pay for 10 months • 2 months FREE
        </div>
      )}

      <div className="flex-grow space-y-3 mt-1">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {plan.plan_name}
            </h3>

            {plan.description && (
              <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                {plan.description}
              </p>
            )}
          </div>

          {plan.plan_id === 'enterprise' && (
            <Award className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          )}
        </div>

        <div>
          <p className="text-xl font-bold text-primary">
            {formattedPrice}
            {price !== 0 && (
              <span className="text-xs font-normal text-muted-foreground ml-1">
                / user / {billingCycle === 'annual' ? 'yr' : 'mo'}
              </span>
            )}
          </p>

          {/* Annual benefit text */}
          {billingCycle === 'annual' && price > 0 && (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1 font-medium">
              Save 2 months — billed once annually
            </p>
          )}

          <p className="text-[11px] text-muted-foreground mt-1 flex items-center">
            <Users className="w-3 h-3 mr-1 text-primary/80" />
            Max seats:&nbsp;
            <span className="font-semibold text-foreground">{seatText}</span>
          </p>
        </div>

        {hasNewFeatures && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80"
          >
            {isExpanded ? (
              <>
                <span>Hide features</span>
                <ArrowRight className="w-3 h-3 rotate-90" />
              </>
            ) : (
              <>
                <span>View key features</span>
                <ArrowRight className="w-3 h-3" />
              </>
            )}
          </button>
        )}

        {isExpanded && (
          <div className="mt-2 border-t border-border pt-2 space-y-1 max-h-36 overflow-auto pr-1">
            {previousPlan && (
              <p className="text-[10px] text-muted-foreground italic mb-1">
                Includes all{" "}
                <span className="font-semibold">{previousPlan.plan_name}</span>{" "}
                features, plus:
              </p>
            )}

            {newFeatures.map(({ key, label }) => (
              <div
                key={key}
                className="flex items-start gap-1.5 text-[11px] text-foreground"
              >
                <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        {isCurrent && (
          <span className={buttonClasses}>
            {isUpgradeMandatory
              ? "Current Plan (Upgrade Required)"
              : "Current Plan"}
          </span>
        )}

        {isDowngrade && !isRenewal && !isCurrent && (
          <span className={buttonClasses}>Downgrade Not Allowed</span>
        )}

        {exceedsLimit && !isCurrent && !isDowngrade && (
          <span className={buttonClasses}>Plan Too Small for Seats</span>
        )}

        {!isCurrent && !isDowngrade && !exceedsLimit && (
          <button
            type='button'
            onClick={handleClick}
            className={buttonClasses}
          >
            {isSelected ? '✓ Selected' : 'Select Plan'}
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------------------------------------------
   Payment Summary Card
--------------------------------------------- */
const PaymentSummaryCard = ({
  calculating,
  priceData,
  priceError = null,
  isProcessing,
  handlePurchase,
  isFreeTrialUpgrade,
  isRenewal,
  isOffline = false,
  pendingRequest = null,
}) => {
  const DetailRow = ({
    label,
    value,
    isBold = false,
    isAccent = false,
    className = "",
  }) => (
    <div className={`flex justify-between items-center py-1.5 ${className}`}>
      <span
        className={`text-xs sm:text-sm text-muted-foreground ${isBold ? "font-semibold text-foreground" : ""
          }`}
      >
        {label}
      </span>
      <span
        className={`text-xs sm:text-sm font-mono ${isBold ? "font-extrabold text-base sm:text-lg" : ""
          } ${isAccent ? "text-primary" : "text-foreground"}`}
      >
        {value}
      </span>
    </div>
  );

  /**
   * Off-platform companies apply to their administrator instead of paying —
   * INCLUDING at renewal.
   *
   * This used to exclude renewals, which was harmless only because an offline
   * company's paid term never actually expired. Now that terms do end, that
   * exclusion routed them into the online renewal checkout: a Razorpay mandate
   * for a company that is billed outside the platform entirely.
   */
  const isOfflineUpgrade = isOffline && !isFreeTrialUpgrade;

  const getButtonLabel = () => {
    if (isProcessing)
      return isOfflineUpgrade ? "Submitting request..." : "Processing payment...";
    if (isOfflineUpgrade)
      return isRenewal ? "Request Renewal from Administrator" : "Apply for Plan Upgrade";
    if (isRenewal) return "Pay & Renew Subscription";
    if (isFreeTrialUpgrade) return "Pay & Activate Subscription";
    // isSeatAddOnly / isCycleSwitch are initialised after the early returns but
    // before render calls this, so the closure references are safe.
    if (isCycleSwitch) return "Pay & Switch to Annual";
    if (isSeatAddOnly) return "Pay & Add Seats";
    return "Pay & Confirm Upgrade";
  };

  if (calculating) {
    return (
      <div className="rounded-2xl border border-border bg-card p-10 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
        <p className="text-sm text-muted-foreground">
          Calculating your subscription charges...
        </p>
      </div>
    );
  }

  if (!priceData) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-8 text-center space-y-4">
        {priceError ? (
          <p className="flex items-start justify-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{priceError}</span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {isOfflineUpgrade
              ? "Pricing for your account is managed by your account manager — no payment is taken online."
              : "Select a plan (and seats, if required) to view the detailed pricing summary."}
          </p>
        )}
        {isOfflineUpgrade && (
          <>
            {pendingRequest ? (
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Request sent — your administrator will apply it once reviewed.
              </p>
            ) : (
              <button
                type="button"
                onClick={handlePurchase}
                disabled={isProcessing}
                className="w-full inline-flex items-center justify-center rounded-xl py-3 text-sm font-semibold shadow-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {isProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Apply for Plan Upgrade
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  const { immediate_charge, recurring_charge, next_billing_date, credit, context, seats } =
    priceData;

  const cgst = immediate_charge.gst?.cgst || 0;
  const sgst = immediate_charge.gst?.sgst || 0;
  const igst = immediate_charge.gst?.igst || 0;

  const isIGST = igst > 0 && cgst === 0 && sgst === 0;
  const immediateGstTotal = cgst + sgst + igst;

  // Prorated mid-cycle upgrade: show the new-plan charge for the remaining days
  // and the credit for the current plan's unused days, netting to what's payable.
  const isUpgrade = !isRenewal && !isFreeTrialUpgrade;
  const isProrated = isUpgrade && context?.upgrade_type === "prorated_upgrade";
  const creditApplied =
    immediate_charge.credit_applied ?? credit?.base_amount ?? 0;
  const grossNewCharge = immediate_charge.new_prorated_charge;
  const daysRemaining = context?.days_remaining;
  const totalCycleDays = context?.total_cycle_days;

  // Same plan + extra seats = a seat ADD, not a plan change. The new-minus-credit
  // presentation is mathematically identical but reads wrong here (the "credit"
  // is just the already-paid seats cancelling themselves out), so show the
  // direct "N seats × rate × days" math instead.
  const extraSeats = Number(seats?.extra_requested || 0);
  const isSeatAddOnly = isProrated && context?.same_plan && extraSeats > 0;

  // Monthly → annual switch: full fresh annual year now, minus a credit for the
  // unused days of the current monthly cycle. Line items are GST-inclusive.
  const isCycleSwitch = isUpgrade && context?.upgrade_type === "cycle_switch";
  const nextBillingLabel = next_billing_date
    ? new Date(next_billing_date).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  /**
   * Which cycle does this purchase land in?
   *
   * The server sends the same `next_billing_date` field either way, but it
   * means two different things: a prorated upgrade or seat add joins the
   * CURRENT cycle and inherits its end date, while a renewal, trial conversion
   * or monthly→annual switch opens a NEW cycle starting today. Showing the bare
   * date left the admin to guess which of the two they were buying.
   */
  const startsNewCycle = isRenewal || isFreeTrialUpgrade || isCycleSwitch;
  const cycleEndLabel = nextBillingLabel || "—";

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Today */}
        <div className="md:col-span-2 border border-primary/20 rounded-xl p-5 bg-primary/5 shadow-sm">
          <h3 className="flex items-center text-sm font-semibold text-primary mb-3">
            <Calendar className="w-4 h-4 mr-2" />
            {isRenewal
              ? "Subscription charge to renew today"
              : isFreeTrialUpgrade
                ? "Subscription charge (starts today)"
                : isCycleSwitch
                  ? "Switching to annual — a full year starts today"
                  : isSeatAddOnly
                    ? "Adding seats — charged only for the days left in this cycle"
                    : isProrated
                      ? "Prorated upgrade — you pay only the difference"
                      : "Your new subscription starts today"}
          </h3>
          {isCycleSwitch ? (
            /* Cycle switch: GST-inclusive line items (annual charge and the
               unused-monthly credit have different GST bases). */
            <div className="space-y-1.5">
              <DetailRow
                label={`Annual plan · ${seats?.after_upgrade ?? ""} seat${
                  (seats?.after_upgrade ?? 0) === 1 ? "" : "s"
                } (incl. GST)`}
                value={formatCurrency(grossNewCharge)}
              />
              <DetailRow
                label="Credit for unused days on current monthly plan"
                value={`− ${formatCurrency(creditApplied)}`}
                className="border-b border-dashed border-border pb-2 text-emerald-600 dark:text-emerald-400"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              {isSeatAddOnly ? (
                <DetailRow
                  label={`${extraSeats} new seat${extraSeats > 1 ? "s" : ""} × ${formatCurrency(
                    immediate_charge.price_per_user
                  )}/user × ${daysRemaining}/${totalCycleDays} days`}
                  value={formatCurrency(immediate_charge.base_amount)}
                />
              ) : (
                <>
                  {isProrated && (
                    <>
                      <DetailRow
                        label={
                          daysRemaining != null
                            ? `New plan (${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left this cycle)`
                            : "New plan (remaining cycle)"
                        }
                        value={formatCurrency(grossNewCharge)}
                      />
                      <DetailRow
                        label="Credit for unused days already paid on current plan"
                        value={`− ${formatCurrency(creditApplied)}`}
                        className="border-b border-dashed border-border pb-2 text-emerald-600 dark:text-emerald-400"
                      />
                    </>
                  )}
                  <DetailRow
                    label={isProrated ? "Amount after credit" : "Base subscription amount"}
                    value={formatCurrency(immediate_charge.base_amount)}
                  />
                </>
              )}
              {isIGST ? (
                <DetailRow
                  label="IGST (18%)"
                  value={formatCurrency(igst)}
                  className="border-b border-dashed border-border pb-2"
                />
              ) : (
                <>
                  <DetailRow label="CGST (9%)" value={formatCurrency(cgst)} />
                  <DetailRow
                    label="SGST (9%)"
                    value={formatCurrency(sgst)}
                    className="border-b border-dashed border-border pb-2"
                  />
                </>
              )}

              <DetailRow
                label="Total GST (18%)"
                value={formatCurrency(immediateGstTotal)}
                className="pt-1"
              />
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-primary/30">
            <DetailRow
              label="Total payable now"
              value={formatCurrency(immediate_charge.payable_now)}
              isBold
              isAccent
            />
          </div>
          {isCycleSwitch && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              A full annual term starts today{nextBillingLabel ? `, renewing ${nextBillingLabel}` : ""}.
              Your subscription will be updated immediately after payment.
            </p>
          )}
          {context?.mandate_method === "upi" && !isCycleSwitch && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Your subscription uses UPI Autopay. Your plan will be updated
              immediately after payment — no additional steps needed.
            </p>
          )}
        </div>

        {/* Recurring */}
        <div className="md:col-span-1 rounded-xl p-4 bg-muted border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Recurring billing
          </h3>
          <DetailRow
            label="Subscription per cycle"
            value={formatCurrency(recurring_charge.base_amount)}
            isBold
          />
          <DetailRow
            label="Approx. with GST"
            value={formatCurrency(recurring_charge.gst?.total || 0)}
          />
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <div className="flex justify-between items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                Billing cycle
              </span>
              <span className="text-xs font-semibold text-foreground text-right capitalize">
                {context?.billing_cycle || "—"}
              </span>
            </div>
            <div className="flex justify-between items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                Applies to
              </span>
              <span className="text-xs font-semibold text-foreground text-right">
                {startsNewCycle ? "New cycle, from today" : "Current cycle"}
              </span>
            </div>
            <div className="flex justify-between items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                {startsNewCycle ? "New cycle ends" : "Current cycle ends"}
              </span>
              <span className="text-xs font-semibold text-primary text-right">
                {cycleEndLabel}
              </span>
            </div>
            <div className="flex justify-between items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                Next billing date
              </span>
              <span className="text-xs font-semibold text-primary text-right">
                {cycleEndLabel}
              </span>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Recurring charges will be auto-collected by Razorpay as per your
            chosen billing cycle.
          </p>
          {isCycleSwitch ? (
            <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
              You're switching to annual now — the year is paid today and renews
              on the date above. Your previous monthly subscription is cancelled.
            </p>
          ) : isSeatAddOnly ? (
            <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
              Your new seats apply immediately, inside the current cycle ending{" "}
              {cycleEndLabel}. Your billing date does not move — from {cycleEndLabel}{" "}
              you'll be billed for {seats?.after_upgrade} seats.
            </p>
          ) : isProrated ? (
            <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
              Your plan upgrades now, inside the current cycle ending {cycleEndLabel}.
              Your billing date does not move — the full new-plan price applies from{" "}
              {cycleEndLabel}.
            </p>
          ) : isRenewal ? (
            <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
              This starts a new cycle today, running to {cycleEndLabel}. Your
              previous subscription is cancelled automatically.
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
              A new cycle starts today and runs to {cycleEndLabel}. Your previous
              subscription will be cancelled automatically and any unused amount
              will be refunded.
            </p>
          )}
        </div>
      </div>

      {isOfflineUpgrade && (
        <p className="text-[11px] text-muted-foreground text-center">
          Indicative pricing. No payment is taken online — your request goes to
          your administrator, who confirms the{" "}
          {isRenewal ? "renewal" : "upgrade"} and billing.
        </p>
      )}

      {isOfflineUpgrade && pendingRequest ? (
        <p className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 text-center text-sm font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
          <CheckCircle className="h-4 w-4 shrink-0" />
          Request sent — your administrator will apply it once reviewed.
        </p>
      ) : (
        <button
          type="button"
          onClick={handlePurchase}
          disabled={isProcessing || !priceData}
          className={`w-full inline-flex items-center justify-center rounded-xl py-3 text-sm font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${isProcessing || !priceData
              ? "bg-muted text-muted-foreground cursor-not-allowed"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}
        >
          {isProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {getButtonLabel()}
          {!isProcessing && <ArrowRight className="w-4 h-4 ml-2" />}
        </button>
      )}

      {isRenewal && (
        <p className="text-[11px] text-muted-foreground text-center">
          By confirming, a new subscription will be started and your company
          access will be restored immediately after payment.
        </p>
      )}
    </div>
  );
};

/* =============================================
   MAIN PAGE: Upgrade / Renew / Manage Plan
============================================= */
const UpgradePlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSubscriptionCancelled = location.state?.isSubscriptionCancelled;
  const reasonFromUrl = location.state?.reason || "manage_subscription";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  const [newSeatsToAdd, setNewSeatsToAdd] = useState(
    reasonFromUrl === "need_to_purchase_seats" ? 1 : 0
  );
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [priceData, setPriceData] = useState(null);
  const [priceError, setPriceError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [manageSeatsOpen, setManageSeatsOpen] = useState(false);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [retryingPaymentId, setRetryingPaymentId] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const {logout} = useAuth()
  // Off-platform (super-admin provisioned) companies can't pay online — they
  // APPLY for an upgrade instead, and the super-admin approves + bills offline.
  const { isPaidOnline } = useSubscription();
  const [pendingRequest, setPendingRequest] = useState(null);
  useEffect(() => {
    if (isPaidOnline) return;
    getMySeatRequest()
      .then((r) => setPendingRequest(r?.pending || null))
      .catch(() => {});
  }, [isPaidOnline]);

  // What an offline company is asking for at renewal. Defaults to "the same
  // again"; they can ask to move plan or add seats in the same request rather
  // than renewing first and applying for a change straight afterwards.
  const [renewalWantsChanges, setRenewalWantsChanges] = useState(false);
  const [renewalPlanId, setRenewalPlanId] = useState("");
  const [renewalExtraSeats, setRenewalExtraSeats] = useState(0);

  const [subStatus, setSubStatus] = useState(null);
  useEffect(() => {
    getSubscriptionStatus()
      .then((s) => {
        setSubStatus(s || null);
      })
      .catch(() => {});
  }, []);

  // Company's own purchase history (seat add-ons, upgrades, cycle switches).
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const HISTORY_PER_PAGE = 5;

  useEffect(() => {
    getPurchaseHistory()
      .then((r) => setHistory(Array.isArray(r?.history) ? r.history : []))
      .catch(() => {});
  }, []);

  // Super-admin-applied changes (kind:"seat_grant") carry their own statuses —
  // approved / pending / rejected — so a straight equality test against the
  // three filter values would hide every one of them behind "All". Group them
  // onto the same three buckets for FILTERING only; the badge and the retry
  // button below still read the raw status, so a rejected request is never
  // rendered as a failed payment.
  const historyStatusBucket = (h) =>
    ({
      approved: 'applied',
      pending: 'pending_payment',
      rejected: 'failed',
    }[h.status] || h.status);

  const filteredHistory = historyStatusFilter === 'all'
    ? history
    : history.filter(h => historyStatusBucket(h) === historyStatusFilter);

  const historyTotalPages = Math.max(1, Math.ceil(filteredHistory.length / HISTORY_PER_PAGE));
  const pagedHistory = filteredHistory.slice(
    (historyPage - 1) * HISTORY_PER_PAGE,
    historyPage * HISTORY_PER_PAGE
  );


  // ── FETCH OPTIONS ─────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const resp = await getUpgradeOptions();
        if (!resp?.plans) throw new Error("Invalid response from server");
        console.log("[UpgradePlan] getUpgradeOptions response:", {
          subscription_status: resp.subscription_status,
          is_expired: resp.is_expired,
        });
        setData(resp);
        setSubscriptionExpired(resp.is_expired || false);

        let defaultPlan =
          resp.default_selected_plan_id || resp.current_plan?.plan_id;

        // Arriving here because the plan cap was hit → preselect the next-larger
        // tier so the admin can upgrade in one step. Unlimited (-1) counts as
        // largest; otherwise pick the smallest limit strictly above current.
        if (reasonFromUrl === "plan_limit_exceeded") {
          const currentLimit = resp.current_plan?.features?.employee_limit ?? 0;
          const bigger = (resp.plans || [])
            .filter((p) => {
              const lim = p?.features?.employee_limit;
              return lim === -1 || (currentLimit !== -1 && lim > currentLimit);
            })
            .sort((a, b) => {
              const la = a.features?.employee_limit;
              const lb = b.features?.employee_limit;
              if (la === -1) return 1; // push unlimited last
              if (lb === -1) return -1;
              return la - lb;
            });
          if (bigger.length) defaultPlan = bigger[0].plan_id;
        }

        setSelectedPlanId(defaultPlan);
        setBillingCycle(
          resp.current_plan?.billing_cycle || resp.billing_cycle || "monthly"
        );
      } catch (e) {
        console.error(e);
        setError("Failed to load subscription options");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const isUnlimitedPlan = (plan) => plan?.features?.employee_limit === -1;

  // ── Derived values ─────────────────────────────
  const currentPlan = data?.current_plan;
  const plans = data?.plans || [];
  const selectedPlan = plans.find((p) => p.plan_id === selectedPlanId);
  const currentActiveSeats = currentPlan?.current_seats || 0;
  const totalNewSeats = currentActiveSeats + newSeatsToAdd;

  const isFreeTrialUpgrade =
    currentPlan?.plan_id === "free-trial" &&
    selectedPlanId &&
    selectedPlanId !== "free-trial";

  // Renewal if explicitly marked OR if subscription is expired (need to renew)
  const isRenewal = reasonFromUrl === "renewal" || subscriptionExpired;

  /**
   * An offline company at renewal has nothing to choose here.
   *
   * Renewing a manually-billed company is the super-admin's action — they set
   * the term and mark it paid. Walking the company admin through plan
   * selection, seats, billing cycle and a price breakdown presents decisions
   * that are not theirs and ends at a payment that cannot be taken. They get a
   * request panel instead of the wizard.
   *
   * A company still on the free trial is excluded: it has no mandate yet
   * either, but converting to paid IS its own online purchase.
   */
  const isOnFreeTrial = currentPlan?.plan_id === "free-trial";
  const isOfflineRenewal = !isPaidOnline && isRenewal && !isOnFreeTrial;

  // What the request will actually carry. Unchanged unless they asked for
  // changes, so the default really is "renew me on what I already had" — the
  // shape the server allows only once the term has ended.
  const renewalCurrentSeats = Number(currentPlan?.current_seats ?? 0);
  const renewalTargetPlanId = renewalWantsChanges
    ? renewalPlanId || currentPlan?.plan_id || ""
    : currentPlan?.plan_id || "";
  const renewalTargetPlan =
    (data?.plans || []).find((p) => p.plan_id === renewalTargetPlanId) || null;
  const renewalSeatsRequested = renewalWantsChanges
    ? renewalCurrentSeats + Number(renewalExtraSeats || 0)
    : renewalCurrentSeats;
  const renewalPlanCap = renewalTargetPlan?.features?.employee_limit ?? null;
  const renewalOverCap =
    renewalPlanCap != null &&
    renewalPlanCap !== -1 &&
    renewalSeatsRequested > renewalPlanCap;
  const renewalIsChanged =
    renewalTargetPlanId !== currentPlan?.plan_id || Number(renewalExtraSeats || 0) > 0;

  const requestOfflineRenewal = async () => {
    if (!renewalTargetPlanId) {
      toast.error("Couldn't read your current plan. Please refresh and try again.");
      return;
    }
    setIsProcessing(true);
    try {
      const resp = await requestPlanUpgrade(
        renewalTargetPlanId,
        renewalWantsChanges ? Number(renewalExtraSeats || 0) : 0
      );
      setPendingRequest(resp?.request || { status: "pending" });
      toast.success(
        renewalIsChanged
          ? "Renewal request sent to your administrator, with your requested changes."
          : "Renewal request sent to your administrator."
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.error ||
          "Could not send the renewal request. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Allow monthly toggle ONLY during renewal (expired/cancelled). For active subscriptions, restrict mid-term switches.
  const allowMonthly = isRenewal ? true : !(currentPlan?.billing_cycle === "annual");

  useEffect(() => {
    if (!selectedPlanId) return;

    const limit = selectedPlan?.features?.employee_limit;

    // Unlimited plan
    if (limit === -1) return;

    // Fail-open if limit is missing/unexpected
    if (typeof limit !== "number") return;

    if (totalNewSeats > limit) {
      setSelectedPlanId(null);
      setPriceData(null);
      toast.info(`Selected plan supports only ${limit} seats. Please choose a higher plan.`);
    }
  }, [selectedPlanId, selectedPlan, totalNewSeats]);

  // ── Upgrade Logic ──────────────────────────────
  const purchaseLogic = useMemo(() => {
    if (!currentPlan)
      return {
        isUpgradeRequired: false,
        isSeatInputRequired: true,
        upgradeReason: "",
      };

    const currentIsUnlimited = isUnlimitedPlan(currentPlan);
    const selectedIsUnlimited = selectedPlan
      ? isUnlimitedPlan(selectedPlan)
      : true;

    let isUpgradeRequired = false;
    let upgradeReason = "";
    let isSeatInputRequired = true;

    if (reasonFromUrl === "plan_limit_exceeded") {
      isUpgradeRequired = true;
      upgradeReason = `Seat limit reached on ${currentPlan.plan_name}. Upgrade required.`;
    } else if (reasonFromUrl === "feature_upgrade_required") {
      isUpgradeRequired = true;
      upgradeReason = "This feature is available only on a higher plan.";
      isSeatInputRequired = true;
    } else if (reasonFromUrl === "renewal") {
      isUpgradeRequired = false;
      upgradeReason = "";
    }

    if (
      reasonFromUrl === "need_to_purchase_seats" &&
      !currentIsUnlimited &&
      totalNewSeats > (currentPlan.features?.employee_limit || 0)
    ) {
      isUpgradeRequired = true;
      upgradeReason = `Adding ${totalNewSeats} seats exceeds your current plan limit of ${currentPlan.features.employee_limit}.`;
    }

    if (
      selectedPlan &&
      !selectedIsUnlimited &&
      totalNewSeats > (selectedPlan.features?.employee_limit || 0)
    ) {
      isUpgradeRequired = true;
      upgradeReason = `Selected plan supports only ${selectedPlan.features.employee_limit} seats.`;
    }

    return { isUpgradeRequired, isSeatInputRequired, upgradeReason };
  }, [reasonFromUrl, currentPlan, selectedPlan, totalNewSeats]);

  // ── Price Calculation ─────────────────────────
  useEffect(() => {
    if (!selectedPlanId || !currentPlan) return;

    if (
      purchaseLogic.isUpgradeRequired &&
      selectedPlanId === currentPlan.plan_id &&
      reasonFromUrl !== "renewal" // renewal can reuse same plan
    )
      return;

    const selectedMaxSeats = selectedPlan?.features?.employee_limit || Infinity;
    if (selectedMaxSeats !== -1 && selectedMaxSeats < totalNewSeats) return;
    if (!allowMonthly && billingCycle === "monthly") return;

    const fetchPrice = async () => {
      setCalculating(true);
      try {
        const result = await calculatePrice({
          target_plan_id: selectedPlanId,
          extra_seats: newSeatsToAdd,
          new_billing_cycle: billingCycle,
        });
        setPriceData(result);
        setPriceError(null);
      } catch (e) {
        console.error(e);
        setPriceData(null);
        setPriceError(
          e?.response?.data?.error ||
            e?.response?.data?.message ||
            null
        );
      } finally {
        setCalculating(false);
      }
    };

    const timer = setTimeout(fetchPrice, 250);
    return () => clearTimeout(timer);
  }, [
    selectedPlanId,
    newSeatsToAdd,
    billingCycle,
    currentPlan,
    purchaseLogic,
    allowMonthly,
    reasonFromUrl,
    totalNewSeats,
    selectedPlan,
  ]);

  // ── Handlers ──────────────────────────────────
  const handleSeatChange = (delta) => {
    setNewSeatsToAdd((prev) => {
      const next = prev + delta;
      if (reasonFromUrl === "need_to_purchase_seats") {
        return Math.max(1, next);
      }
      return Math.max(0, next);
    });
  };

  const handleBack = () => {
    if (isSubscriptionCancelled) {
      logout();
      navigate("/");
    } else {
      navigate(-1);
    }
  };

  const refreshPurchaseData = async () => {
    try {
      const [historyResp, statusResp, optionsResp] = await Promise.all([
        getPurchaseHistory().catch((err) => { console.error("History refresh failed:", err); return null; }),
        getSubscriptionStatus().catch((err) => { console.error("Status refresh failed:", err); return null; }),
        getUpgradeOptions().catch((err) => { console.error("Options refresh failed:", err); return null; }),
      ]);

      if (historyResp?.history) setHistory(historyResp.history);
      if (statusResp) setSubStatus(statusResp);
      if (optionsResp?.plans) setData(optionsResp);

      // Returned so callers can rebase the selection on the plan the company
      // now actually holds, rather than on stale closure state.
      return optionsResp || null;
    } catch (err) {
      console.error("Failed to refresh purchase data:", err);
      return null;
    }
  };

  /**
   * A purchase completed — refresh everything and put the wizard back to a
   * clean step 1.
   *
   * The reset matters as much as the redirect: without it the user lands on
   * step 1 still holding the selection they just bought — the old plan
   * highlighted, seats-to-add still counting, and a stale price quote for a
   * purchase that has already happened. Rebasing on the REFRESHED options
   * response makes step 1 reflect what they now own.
   */
  const completePurchaseAndReset = async () => {
    const fresh = await refreshPurchaseData();

    setNewSeatsToAdd(0);
    setPriceData(null);
    setPriceError(null);
    setManageSeatsOpen(false);

    if (fresh?.plans) {
      setSelectedPlanId(
        fresh.default_selected_plan_id || fresh.current_plan?.plan_id || null
      );
      setBillingCycle(
        fresh.current_plan?.billing_cycle || fresh.billing_cycle || "monthly"
      );
      setSubscriptionExpired(fresh.is_expired || false);
    }

    setCurrentStep(1);
  };

  const launchUpgradeCheckout = (resp, { onConfirmed, setProcessing }) => {
    const { razorpay_order_id, amount, amount_paise, kind } = resp;
    const isAddonOrder = kind === "seat_addon";

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      order_id: razorpay_order_id,
      amount: amount_paise ?? Math.round((amount || 0) * 100),
      currency: "INR",

      name: "Element Tree – Zenith Payroll",
      description: isAddonOrder
        ? "Add employee seats (prorated)"
        : "Plan upgrade (prorated)",

      image:
        "https://elementree-website-assets.s3.ap-south-1.amazonaws.com/images/logo/Logo+with+Name+and+Slogan+-+Full+Colour.png",

      handler: async function (rzpResponse) {
        try {
          const confirmPayload = {
            razorpay_order_id,
            razorpay_payment_id: rzpResponse.razorpay_payment_id,
            razorpay_signature: rzpResponse.razorpay_signature,
          };
          const confirmResp = isAddonOrder
            ? await confirmSeatAddOn(confirmPayload)
            : await confirmUpgrade(confirmPayload);

          console.log("Payment confirm response:", { isAddonOrder, confirmResp });

          if (confirmResp?.applied) {
            console.log("Payment applied successfully, refreshing data...");
            await onConfirmed();
          } else {
            console.log("Payment not applied, showing info toast. Response:", confirmResp);
            setProcessing(false);
            toast.info("Payment received and being verified. Checking for updates...");
            // Money was captured; only our confirmation is outstanding. Treat
            // it as success for navigation — leaving the user on the purchase
            // form after they have paid reads as though it did not go through.
            setCurrentStep(1);
            setTimeout(() => {
              completePurchaseAndReset();
            }, 3000);
          }
        } catch (err) {
          console.error("Upgrade confirm failed:", {
            status: err?.response?.status,
            error: err?.response?.data?.error,
            message: err?.message,
            fullResponse: err?.response?.data,
          });
          const errorMsg = err?.response?.data?.error || err?.message;

          if (errorMsg?.includes("Mandate") || errorMsg?.includes("authorization")) {
            setProcessing(false);
            toast.info("Payment received and being verified. Checking for updates...");
            setCurrentStep(1);
            setTimeout(() => {
              completePurchaseAndReset();
            }, 3000);
          } else {
            // Real failure — stay put so the user's selection survives and a
            // retry costs one click.
            setProcessing(false);
            toast.error(
              `Payment verification failed: ${errorMsg || "Unknown error"}. Please contact support if amount was deducted.`
            );
          }
        }
      },

      modal: {
        ondismiss: () => {
          setProcessing(false);
          toast.info("Payment cancelled.");
        },
      },

      theme: { color: "#3194A0" },
    };

    new window.Razorpay(options).open();
  };

  const handleConfirmRetry = async () => {
    if (!retryingPaymentId) return;
    setRetryLoading(true);
    try {
      const resp = await retryPurchase(retryingPaymentId);
      if (resp?.no_charge) {
        toast.success("Payment retried and applied successfully.");
        setRetryConfirmOpen(false);
        await completePurchaseAndReset();
        return;
      }
      setRetryConfirmOpen(false);
      launchUpgradeCheckout(resp, {
        setProcessing: setRetryLoading,
        onConfirmed: async () => {
          toast.success("Payment successful — your purchase has been applied.");
          // A retry that lands is still a completed purchase, so the wizard is
          // stale in the same way — reset it rather than only refreshing.
          await completePurchaseAndReset();
        },
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Could not retry this payment. Please try again."
      );
    } finally {
      setRetryLoading(false);
      setRetryingPaymentId(null);
    }
  };

  const handlePurchase = async () => {
    if (!priceData || !selectedPlanId) {
      toast.error("Please select a plan first.");
      return;
    }

    try {
      setIsProcessing(true);

      // 0️⃣ OFF-PLATFORM COMPANY → APPLY for the upgrade instead of paying.
      // No Razorpay subscription exists to bill against; the request goes to
      // the super-admin, who applies the plan change and bills offline.
      // Renewal included: an offline company has no mandate to charge, so the
      // request path is its ONLY route — see isOfflineUpgrade above.
      if (!isFreeTrialUpgrade && !isPaidOnline) {
        try {
          const resp = await requestPlanUpgrade(selectedPlanId, newSeatsToAdd);
          setPendingRequest(resp?.request || { status: "pending" });
          toast.success(
            isRenewal
              ? "Renewal request sent to your administrator."
              : "Upgrade request sent to your administrator."
          );
          navigate("/dashboard");
        } catch (e) {
          const msg =
            e?.response?.data?.error ||
            "Could not submit the upgrade request. Please try again.";
          toast.error(msg);
        } finally {
          setIsProcessing(false);
        }
        return;
      }

      // 3️⃣ NORMAL UPGRADE / SEAT ADD / PLAN CHANGE
      // Prorated: pay only the difference now via a one-time ORDER. The plan is
      // switched (and the billing date kept) once the payment is confirmed.
      if (!isFreeTrialUpgrade && !isRenewal) {
        const payload = {
          target_plan_id: selectedPlanId,
          extra_seats: newSeatsToAdd,
          new_billing_cycle: billingCycle,
        };

        let resp;
        try {
          resp = await createUpgradeSubscription(payload);
        } catch (e) {
          const msg = e?.response?.data?.error || "";
          // An abandoned earlier attempt blocks new ones — cancel it and retry.
          if (e?.response?.status === 409 && msg.includes("already awaiting payment")) {
            // For seat add-ons (same plan, extra seats), auto-cancel; for plan changes, ask first
            const isSeatAddOnly = selectedPlanId === currentPlan?.plan_id && newSeatsToAdd > 0;

            if (!isSeatAddOnly) {
              const ok = window.confirm(
                "An unfinished upgrade payment already exists. Cancel it and start over?"
              );
              if (!ok) {
                setIsProcessing(false);
                return;
              }
            }

            await cancelPendingUpgrade();
            resp = await createUpgradeSubscription(payload);
          } else {
            throw e;
          }
        }

        // ₹0 prorated difference — plan switched immediately, no checkout needed.
        if (resp?.no_charge) {
          toast.success("Plan upgraded successfully.");
          setIsProcessing(false);
          await completePurchaseAndReset();
          return;
        }

        const { razorpay_order_id, amount, amount_paise, kind } = resp;
        if (!razorpay_order_id) {
          throw new Error("Could not start the upgrade payment.");
        }
        launchUpgradeCheckout(resp, {
          setProcessing: setIsProcessing,
          onConfirmed: async () => {
            const isAddonOrder = kind === "seat_addon";
            toast.success(
              isAddonOrder
                ? "Seats added successfully."
                : "Plan upgraded successfully."
            );
            setIsProcessing(false);
            // Back to a clean step 1 showing what they now own. On failure we
            // deliberately stay on step 2 with the selection intact, so a retry
            // is one click away.
            await completePurchaseAndReset();
          },
        });
        return;
      }

      // 1️⃣ FREE TRIAL → PAID  /  2️⃣ RENEW — subscription (recurring) checkout
      let resp;
      if (isFreeTrialUpgrade) {
        resp = await createFreeTrialToPaidSubscription({
          target_plan_id: selectedPlanId,
          billing_cycle: billingCycle,
          employee_count: totalNewSeats,
        });
      } else {
        resp = await renewSubscription({
          target_plan_id: selectedPlanId,
          billing_cycle: billingCycle,
          employee_count: totalNewSeats,
        });
      }

      const { razorpay_subscription_id, payment_id } = resp;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        subscription_id: razorpay_subscription_id,

        name: "Element Tree – Zenith Payroll",
        description: isRenewal
          ? "Renew Subscription"
          : "Subscription Payment",

        image:
          "https://elementree-website-assets.s3.ap-south-1.amazonaws.com/images/logo/Logo+with+Name+and+Slogan+-+Full+Colour.png",

        notes: {
          razorpay_subscription_id,
        },

        handler: async function (rzpResponse) {
          try {
            await confirmPayment({
              payment_id,
              razorpay_subscription_id,
              razorpay_payment_id: rzpResponse.razorpay_payment_id,
              razorpay_signature: rzpResponse.razorpay_signature,
            });

            toast.success(
              isRenewal
                ? "Subscription renewed successfully."
                : "Subscription updated successfully."
            );

            setIsProcessing(false);
            await completePurchaseAndReset();
          } catch (err) {
            console.error("Confirm failed:", err);
            const errorMsg = err?.response?.data?.error || err?.message;

            // If it's a mandate/authorization error, it's actually OK (webhook will process)
            if (errorMsg?.includes("Mandate") || errorMsg?.includes("authorization")) {
              toast.info("Payment received and being verified. Checking for updates...");
              setIsProcessing(false);
              // The money WAS captured — only our confirmation is pending — so
              // this counts as success for navigation. The reset below fills
              // in the rest once the backend catches up.
              setCurrentStep(1);
              setTimeout(() => {
                completePurchaseAndReset();
              }, 3000);
            } else {
              // Real failure — stay on step 2 with the selection intact so the
              // user can retry without rebuilding it.
              toast.error(
                `Payment verification failed: ${errorMsg || "Unknown error"}. Please contact support if amount was deducted.`
              );
              setIsProcessing(false);
            }
          } finally {
            setIsProcessing(false);
          }
        },

        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            toast.info("Payment cancelled.");
          },
        },

        theme: { color: "#3194A0" },
      };

      new window.Razorpay(options).open();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to start payment. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // ── Loading / Error ───────────────────────────
  if (loading) {
    return (
      <PageShell
        logout={logout}
        isSubscriptionCancelled={isSubscriptionCancelled}
        title="Subscription Management"
        onBack={handleBack}
      >
        <div className="flex items-center justify-center py-20">
          <div className="text-center text-muted-foreground">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
            <p className="font-medium">Loading subscription options...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell onBack={handleBack} title="Subscription Management">
        <div className="flex flex-col items-center justify-center py-20 text-red-600 dark:text-red-400">
          <AlertTriangle className="w-10 h-10 mb-3" />
          <p className="font-medium text-sm">{error}</p>
        </div>
      </PageShell>
    );
  }

  if (!currentPlan) {
    return (
      <PageShell onBack={handleBack} title="Subscription Management">
        <div className="py-20 text-center text-muted-foreground">
          <p className="text-sm">
            No subscription context found. Please contact support.
          </p>
        </div>
      </PageShell>
    );
  }

  const isManageSubscription = reasonFromUrl === "manage_subscription";

  const hasChanges =
    selectedPlanId !== currentPlan.plan_id ||
    newSeatsToAdd > 0 ||
    billingCycle !== (currentPlan.billing_cycle || "monthly");

  const canGoNext =
    selectedPlanId &&
    selectedPlanId !== "free-trial" &&
    (!purchaseLogic.isUpgradeRequired ||
      selectedPlanId !== currentPlan.plan_id ||
      isRenewal) &&
    (!isManageSubscription || hasChanges || isRenewal);

  if (selectedPlanId && currentPlan) {
    console.log("[UpgradePlan] canGoNext debug:", {
      selectedPlanId,
      currentPlan: currentPlan.plan_id,
      isRenewal,
      subscriptionExpired,
      isManageSubscription,
      hasChanges,
      purchaseLogic_isUpgradeRequired: purchaseLogic.isUpgradeRequired,
      canGoNext,
    });
  }

  const seatsMin = reasonFromUrl === "need_to_purchase_seats" ? 1 : 0;

  // ── OFFLINE COMPANY AT RENEWAL ─────────────────
  // No plan picker, no pricing, no checkout — see isOfflineRenewal above.
  if (isOfflineRenewal) {
    return (
      <PageShell
        onBack={handleBack}
        title="Subscription Management"
        subtitle="Your subscription is billed offline by your administrator."
      >
        {/* Constrained: the content is a short form, and stretched across a
            desktop width its controls read as a banner rather than a form. */}
        <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-border bg-card overflow-hidden">
          {/* Who does what — stated once, at the top, not as an alert. */}
          <div className="flex items-start gap-3 border-b border-border px-5 py-4 sm:px-6">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-500/20 bg-amber-500/10">
              <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">
                Your administrator renews this subscription
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                This company is billed outside the platform, so there's nothing to
                pay here. Send a request and they'll take it from there.
              </p>
            </div>
            <span className="ml-auto hidden shrink-0 rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground sm:block">
              Offline billing
            </span>
          </div>

          {/* The facts, as a scannable strip rather than a sentence. Each cell
              answers one question the admin would otherwise have to hunt for. */}
          <dl className="grid grid-cols-2 divide-x divide-y divide-border border-b border-border sm:grid-cols-3 sm:divide-y-0">
            <div className="px-5 py-3 sm:px-6">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Plan
              </dt>
              <dd className="mt-0.5 text-sm font-semibold text-foreground">
                {currentPlan?.plan_name || "—"}
              </dd>
            </div>
            <div className="px-5 py-3 sm:px-6">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Billing
              </dt>
              <dd className="mt-0.5 text-sm font-semibold capitalize text-foreground">
                {currentPlan?.billing_cycle || "—"}
                <span className="font-normal text-muted-foreground">
                  {" · "}{renewalCurrentSeats} seat{renewalCurrentSeats === 1 ? "" : "s"}
                </span>
              </dd>
            </div>
            <div className="col-span-2 px-5 py-3 sm:col-span-1 sm:px-6">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Access until
              </dt>
              <dd className="mt-0.5 text-sm font-semibold text-foreground">
                {subStatus?.access_until ? (
                  <>
                    {new Date(subStatus.access_until).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {subStatus?.grace_days ? (
                      <span className="font-normal text-muted-foreground">
                        {" · "}{subStatus.grace_days}-day grace
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="font-normal text-muted-foreground">
                    Until your administrator confirms
                  </span>
                )}
              </dd>
            </div>
          </dl>

          <div className="space-y-5 px-5 py-5 sm:px-6">
          {/* Renewal is also the natural moment to ask for a bigger plan or
              more seats. Asking here saves renewing first and applying for the
              change immediately afterwards — the administrator sees one
              request. No prices: this company may be on a negotiated rate, so
              quoting list pricing here would be wrong more often than right. */}
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              What should we ask for?
            </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                key: "same",
                on: !renewalWantsChanges,
                title: "Renew as it is",
                detail: currentPlan
                  ? `${currentPlan.plan_name} · ${renewalCurrentSeats} seat${renewalCurrentSeats === 1 ? "" : "s"}`
                  : "Same plan and seats",
              },
              {
                key: "change",
                on: renewalWantsChanges,
                title: "Renew with changes",
                detail: "Move to a different plan, or add seats",
              },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setRenewalWantsChanges(opt.key === "change")}
                className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                  opt.on
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                      opt.on ? "border-primary" : "border-muted-foreground/40"
                    }`}
                  >
                    {opt.on && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {opt.title}
                  </span>
                </span>
                <span className="mt-1 block pl-6 text-xs text-muted-foreground">
                  {opt.detail}
                </span>
              </button>
            ))}
          </div>
          </div>

          {renewalWantsChanges && (
            <div className="grid gap-4 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-[1fr_auto]">
              <div className="space-y-1.5">
                <label
                  htmlFor="renewal-plan"
                  className="text-xs font-semibold text-foreground"
                >
                  Plan
                </label>
                <select
                  id="renewal-plan"
                  value={renewalTargetPlanId}
                  onChange={(e) => setRenewalPlanId(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  {(data?.plans || []).map((p) => (
                    <option key={p.plan_id} value={p.plan_id}>
                      {p.plan_name}
                      {p.features?.employee_limit === -1
                        ? " · unlimited seats"
                        : p.features?.employee_limit != null
                          ? ` · up to ${p.features.employee_limit} seats`
                          : ""}
                      {p.plan_id === currentPlan?.plan_id ? " (current)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sized to its content. A seat count stretched to the width of
                  the card reads as a text field, not a stepper. */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">
                  Extra seats
                </label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={Number(renewalExtraSeats) <= 0}
                    onClick={() =>
                      setRenewalExtraSeats((s) => Math.max(0, Number(s) - 1))
                    }
                    className="h-9 w-9 shrink-0 rounded-lg border border-border text-foreground hover:bg-muted transition disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Fewer seats"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={0}
                    value={renewalExtraSeats}
                    onChange={(e) =>
                      setRenewalExtraSeats(Math.max(0, Number(e.target.value) || 0))
                    }
                    className="w-16 rounded-lg border border-border bg-background px-2 py-2 text-sm text-center tabular-nums"
                  />
                  <button
                    type="button"
                    onClick={() => setRenewalExtraSeats((s) => Number(s) + 1)}
                    className="h-9 w-9 shrink-0 rounded-lg border border-border text-foreground hover:bg-muted transition"
                    aria-label="More seats"
                  >
                    +
                  </button>
                </div>
              </div>

              {renewalOverCap && (
                <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 sm:col-span-2 dark:border-red-800/70 dark:bg-red-950/40 dark:text-red-300">
                  {renewalTargetPlan?.plan_name} allows up to {renewalPlanCap} seats.
                  Choose a bigger plan or ask for fewer seats.
                </p>
              )}
            </div>
          )}

          {/* One line restating the whole request, so the button is never a
              leap of faith. Replaces the duplicated plan/seat summary. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-xl bg-muted/60 px-4 py-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Requesting
            </span>
            <span className="text-sm font-semibold text-foreground">
              {renewalTargetPlan?.plan_name || currentPlan?.plan_name || "—"}
              <span className="font-normal text-muted-foreground">
                {" · "}{renewalSeatsRequested} seat{renewalSeatsRequested === 1 ? "" : "s"}
                {renewalIsChanged ? "" : " · unchanged"}
              </span>
            </span>
          </div>

          {pendingRequest ? (
            /* No spinner. A spinning loader promises something is in progress
               and about to finish; this is waiting on a person to make a
               decision, with no turnaround we can honestly imply. The request
               itself IS complete, so it reads as done, not as loading. */
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <span className="font-medium">Request sent to your administrator.</span>{" "}
                They'll apply it when they've reviewed it. Your access continues
                in the meantime.
              </span>
            </div>
          ) : (
            <div className="flex flex-col-reverse items-center gap-3 border-t border-border pt-4 sm:flex-row sm:justify-between">
              <p className="text-xs text-muted-foreground">
                No payment is taken here — your administrator confirms the
                billing.
              </p>
              <button
                type="button"
                onClick={requestOfflineRenewal}
                disabled={isProcessing || renewalOverCap || !renewalTargetPlanId}
                className={`inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-auto ${
                  isProcessing || renewalOverCap || !renewalTargetPlanId
                    ? "bg-muted text-muted-foreground cursor-not-allowed"
                    : "bg-primary text-primary-foreground hover:bg-primary/90"
                }`}
              >
                {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
                {isProcessing
                  ? "Sending request..."
                  : renewalIsChanged
                    ? "Request renewal with changes"
                    : "Request renewal"}
                {!isProcessing && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          )}
          </div>
        </div>
      </PageShell>
    );
  }

  // ── MAIN UI ────────────────────────────────────
  return (
    <PageShell
      onBack={handleBack}
      title="Subscription Management"
      subtitle="Upgrade your plan, add seats, or renew your subscription in a single, guided flow."
    >
      {!isManageSubscription && (
        <ReasonBanner reason={reasonFromUrl} currentPlan={currentPlan} />
      )}

      {subscriptionExpired && (
        <div className="mt-4 p-4 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-sm text-orange-800 dark:text-orange-200">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">
                Your subscription has expired
              </p>
              <p className="mt-1 text-xs opacity-90">
                Renew your subscription to continue using the service. You can change your plan, adjust seats, switch between monthly/annual billing, or downgrade to a lower tier.
              </p>
            </div>
          </div>
        </div>
      )}

      {isManageSubscription && !subscriptionExpired && (
        <div className="mt-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-200">
          <p className="font-medium">
            You’re managing your current subscription.
          </p>
          <p className="mt-1 text-xs opacity-90">
            Change your plan, add seats, or switch billing cycle to see updated
            pricing and proceed.
          </p>
        </div>
      )}
      {/* STEP 1 – PLAN & SEATS */}
      {currentStep === 1 && (
        <>
          {/* Current plan + billing cycle */}
          <div className="rounded-2xl border border-border bg-card shadow-sm p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Package className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Current plan
                  </p>
                  {subStatus?.status && (
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold capitalize text-primary">
                      {subStatus.status}
                    </span>
                  )}
                </div>
                <p className="text-base font-semibold text-foreground">
                  {currentPlan.plan_name}
                </p>

                {/* Stats */}
                {(() => {
                  const cycle = (currentPlan.billing_cycle || "monthly");
                  const rate =
                    cycle === "annual"
                      ? currentPlan.price_per_user_annual
                      : currentPlan.price_per_user_monthly;
                  const limit = currentPlan.features?.employee_limit;
                  const seatsLabel =
                    limit === -1 || limit == null
                      ? `${currentActiveSeats} seats`
                      : `${currentActiveSeats} / ${limit} seats`;
                  const nbd = subStatus?.next_billing_date;
                  return (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="text-foreground font-medium">{seatsLabel}</span>
                      <span className="text-border">·</span>
                      <span className="capitalize">{cycle}</span>
                      {rate > 0 && (
                        <>
                          <span className="text-border">·</span>
                          <span>
                            ₹{rate}/user/{cycle === "annual" ? "yr" : "mo"}
                          </span>
                        </>
                      )}
                      {nbd && (
                        <>
                          <span className="text-border">·</span>
                          <span>
                            {subStatus?.auto_renew === false
                              ? "Ends"
                              : "Renews"}{" "}
                            {new Date(nbd).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="flex flex-col items-start lg:items-end gap-1.5 shrink-0">
              <div className="inline-flex rounded-full p-1 bg-muted text-xs">
                {["monthly", "annual"].map((cycle) => {
                  // For an active MONTHLY subscription, switching to annual is
                  // allowed (starts a fresh annual term). Switching an annual
                  // sub back to monthly mid-term is not — that stays disabled.
                  const current = currentPlan.billing_cycle || "monthly";
                  const upgradeMode = !isRenewal && !isFreeTrialUpgrade;
                  const disabled = upgradeMode
                    ? current === "annual" && cycle === "monthly"
                    : !allowMonthly && cycle === "monthly";

                  console.log(`[BillingToggle] cycle=${cycle}, current=${current}, isRenewal=${isRenewal}, upgradeMode=${upgradeMode}, disabled=${disabled}`);
                  return (
                    <button
                      key={cycle}
                      type="button"
                      onClick={() => !disabled && setBillingCycle(cycle)}
                      disabled={disabled}
                      className={`px-3.5 py-1.5 rounded-full font-medium capitalize flex items-center gap-1 transition ${
                        billingCycle === cycle
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      {cycle === "annual" && <Calendar className="w-3.5 h-3.5" />}
                      {cycle}
                    </button>
                  );
                })}
              </div>
              {!isRenewal &&
                !isFreeTrialUpgrade &&
                (currentPlan.billing_cycle || "monthly") === "annual" && (
                  <span className="text-[10px] text-muted-foreground">
                    Switching to monthly takes effect at renewal.
                  </span>
                )}
            </div>
          </div>

          {/* Payment Method */}
          {subStatus && (
            <div className="rounded-2xl border border-border bg-card shadow-sm p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h10m4 0a1 1 0 11-2 0 1 1 0 012 0z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Payment Method
                      </p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <svg className="w-3.5 h-3.5 text-muted-foreground cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Updates available at renewal</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-base font-semibold text-foreground mt-2">
                      {!subStatus.is_paid_online
                        ? "Offline Payment"
                        : subStatus.payment_method ?
                          (subStatus.payment_method === "upi" ? "UPI" :
                           subStatus.payment_method === "card" ? "Debit Card" :
                           subStatus.payment_method === "emandate" ? "Card e-Mandate" :
                           subStatus.payment_method.charAt(0).toUpperCase() + subStatus.payment_method.slice(1))
                          : "Not set"}
                    </p>
                    {subStatus.is_paid_online && subStatus.payment_method && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {subStatus.payment_method === "upi"
                          ? "Auto-deducted on renewal date"
                          : `Last used on ${new Date(subStatus.last_charged_at).toLocaleDateString("en-IN")}`}
                      </p>
                    )}
                    {!subStatus.is_paid_online && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Managed outside the platform by super-admin
                      </p>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Seats */}
          {purchaseLogic.isSeatInputRequired && (
            <section className="rounded-2xl border border-border bg-card shadow-sm p-6">
              <StepHeader
                step={1}
                title="Add employees"
                subtitle="Choose how many employees your subscription should cover."
              />
              <div className="flex flex-col sm:flex-row sm:items-end gap-5">
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-muted-foreground">
                    Employees to {reasonFromUrl === "renewal" ? "include" : "add"}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleSeatChange(-1)}
                      disabled={newSeatsToAdd <= seatsMin}
                      className="p-2.5 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4 text-foreground" />
                    </button>

                    <input
                      type="number"
                      min={seatsMin}
                      value={newSeatsToAdd}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isNaN(v)) {
                          setNewSeatsToAdd(seatsMin);
                          return;
                        }
                        if (reasonFromUrl === "need_to_purchase_seats") {
                          setNewSeatsToAdd(Math.max(1, v));
                        } else {
                          setNewSeatsToAdd(Math.max(0, v));
                        }
                      }}
                      className="w-20 text-center text-xl font-extrabold p-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                    />

                    <button
                      type="button"
                      onClick={() => handleSeatChange(1)}
                      className="p-2.5 rounded-full border border-border bg-card hover:bg-muted"
                    >
                      <Plus className="w-4 h-4 text-foreground" />
                    </button>
                  </div>
                </div>

                <div className="rounded-xl bg-muted px-4 py-3 text-sm">
                  <span className="text-muted-foreground">Total after change: </span>
                  <span className="font-bold text-primary">
                    {totalNewSeats} employees
                  </span>
                </div>
              </div>

              {purchaseLogic.isUpgradeRequired && (
                <div className="mt-4 p-3 rounded-xl border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-800 dark:text-amber-200 flex gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Upgrade action required</p>
                    <p className="mt-0.5">{purchaseLogic.upgradeReason}</p>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Plans — full width */}
          <section className="rounded-2xl border border-border bg-card shadow-sm p-6">
            <StepHeader
              step={purchaseLogic.isSeatInputRequired ? 2 : 1}
              title={isRenewal ? "Choose plan to renew" : "Choose your plan"}
              subtitle="Compare plans and select the one that matches your requirements."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <PlanCard
                  key={plan.plan_id}
                  plan={plan}
                  allPlans={plans}
                  billingCycle={billingCycle}
                  isCurrent={plan.plan_id === currentPlan.plan_id}
                  isSelected={selectedPlanId === plan.plan_id}
                  onSelect={setSelectedPlanId}
                  isUpgradeMandatory={purchaseLogic.isUpgradeRequired}
                  isDowngrade={plan.display_order < currentPlan.display_order}
                  isRenewal={isRenewal}
                  exceedsLimit={
                    !isUnlimitedPlan(plan) &&
                    totalNewSeats > (plan.features?.employee_limit || 0)
                  }
                />
              ))}
            </div>
          </section>

          {/* Recap + Continue */}
          <div className="rounded-2xl border border-border bg-card shadow-sm p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <SummaryItem
                label="Plan"
                value={selectedPlan?.plan_name || "Not selected"}
              />
              <SummaryItem
                label="Billing"
                value={billingCycle === "annual" ? "Annual" : "Monthly"}
              />
              <SummaryItem label="Seats" value={totalNewSeats} />
              {priceData && (
                <SummaryItem
                  label="Est. today"
                  value={formatCurrency(priceData.immediate_charge.payable_now)}
                  accent
                />
              )}
              {priceData && (
                <SummaryItem
                  label="Next billing"
                  value={new Date(
                    priceData.next_billing_date,
                  ).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                />
              )}
            </div>

            <button
              type="button"
              disabled={!canGoNext}
              onClick={() => {
                if (!selectedPlanId) {
                  toast.error("Please select a plan before proceeding.");
                  return;
                }
                if (isManageSubscription && !hasChanges) {
                  toast.info(
                    "Make a change (plan, seats, or billing cycle) to review updated charges.",
                  );
                  return;
                }
                setCurrentStep(2);
              }}
              className={`shrink-0 inline-flex items-center justify-center rounded-xl px-6 py-2.5 text-sm font-semibold shadow-sm transition ${
                !canGoNext
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              Review charges
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </>
      )}

      {/* STEP 2 – REVIEW & PAY */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold tracking-wide mb-2">
                STEP 3
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                Review charges & confirm payment
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Verify the immediate charge and your recurring subscription
                details before proceeding to payment.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              Back to plans
            </button>
          </div>

          <PaymentSummaryCard
            calculating={calculating}
            priceData={priceData}
            priceError={priceError}
            isProcessing={isProcessing}
            handlePurchase={handlePurchase}
            isFreeTrialUpgrade={isFreeTrialUpgrade}
            isRenewal={isRenewal}
            isOffline={!isPaidOnline}
            pendingRequest={pendingRequest}
          />
        </div>
      )}

      {/* Purchase history — the company's own add-ons, upgrades, cycle switches */}
      {history.length > 0 && (
        <section className="rounded-2xl border border-border bg-card shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Purchase history
              </h2>
              <p className="text-xs text-muted-foreground">
                Seats, upgrades and billing-cycle changes on your subscription —
                including changes applied by your administrator.
              </p>
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 items-center">
              <Select
                value={historyStatusFilter}
                onValueChange={(value) => {
                  setHistoryStatusFilter(value);
                  setHistoryPage(1);
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="applied">Successful</SelectItem>
                  <SelectItem value="pending_payment">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              {historyStatusFilter !== 'all' && (
                <button
                  onClick={() => {
                    setHistoryStatusFilter('all');
                    setHistoryPage(1);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted rounded transition"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-semibold">Date</th>
                  <th className="py-2 pr-3 font-semibold">What changed</th>
                  <th className="py-2 pr-3 font-semibold text-center">Status</th>
                  <th className="py-2 pr-3 font-semibold">Payment method</th>
                  <th className="py-2 pl-3 font-semibold text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {pagedHistory.map((p) => {
                  const kind = p.kind || "seat_addon";
                  const cap = (c) =>
                    c ? c.charAt(0).toUpperCase() + c.slice(1) : "";
                  let label;
                  if (kind === "seat_addon") {
                    label = `Added ${p.extra_employees} seat${
                      p.extra_employees === 1 ? "" : "s"
                    } (${p.employees_before} → ${p.employees_after})`;
                  } else if (kind === "cycle_switch") {
                    label = `Switched to ${cap(p.to_billing_cycle)} billing${
                      p.from_plan_name !== p.to_plan_name
                        ? ` · ${p.from_plan_name} → ${p.to_plan_name}`
                        : ` · ${p.to_plan_name}`
                    }`;
                  } else if (kind === "renewal") {
                    label = `Renewed ${p.plan_name} (${cap(p.billing_cycle)} billing)`;
                  } else if (kind === "offline_purchase") {
                    // A payment recorded by the administrator against an
                    // invoice settled outside the platform. Labelled from the
                    // purpose ("Seat add-on", "Plan upgrade", …) because a
                    // Payment carries none of the before/after seat and plan
                    // fields the self-serve kinds above render from.
                    label = `${p.purpose_label} · ${p.plan_name} · Recorded by your administrator`;
                  } else if (kind === "seat_grant") {
                    // Applied by the super-admin, not bought here. Say so — the
                    // admin did not make this change themselves and otherwise
                    // has no way to tell it apart from their own purchases.
                    const by =
                      p.status === "approved" ? "Applied by your administrator" : null;
                    if (p.request_type === "plan_upgrade") {
                      label = `Plan changed ${p.from_plan_name} → ${p.to_plan_name}`;
                    } else if (p.request_type === "custom_price") {
                      label = `Rate changed${
                        p.price_per_user ? ` to ${formatCurrency(p.price_per_user)}/user` : ""
                      }`;
                    } else {
                      label = `Seats ${p.seats_before} → ${p.seats_after}`;
                    }
                    if (by) label += ` · ${by}`;
                  } else {
                    label = `Upgraded ${p.from_plan_name} → ${p.to_plan_name}`;
                  }
                  const getStatusBadge = () => {
                    switch (p.status) {
                      case "applied":
                        return (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            ✓ Applied
                          </span>
                        );
                      case "pending_payment":
                        return (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            ✓ Captured
                          </span>
                        );
                      case "failed":
                        return (
                          <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2.5 py-1 text-[10px] font-semibold text-red-700 dark:text-red-300">
                            ✕ Failed
                          </span>
                        );
                      // Super-admin-applied changes (kind:"seat_grant").
                      case "approved":
                        return (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            ✓ Applied
                          </span>
                        );
                      case "pending":
                        return (
                          <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                            ⏳ Awaiting approval
                          </span>
                        );
                      case "rejected":
                        return (
                          <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2.5 py-1 text-[10px] font-semibold text-red-700 dark:text-red-300">
                            ✕ Declined
                          </span>
                        );
                      default:
                        return (
                          <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-900/30 px-2.5 py-1 text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                            {p.status}
                          </span>
                        );
                    }
                  };
                  const statusBadge = getStatusBadge();

                  return (
                    <tr
                      key={p._id}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className="py-2.5 pr-3 whitespace-nowrap text-muted-foreground">
                        {p.applied_at || p.created_at
                          ? new Date(p.applied_at || p.created_at).toLocaleDateString(
                              "en-IN",
                              { day: "numeric", month: "short", year: "numeric" }
                            )
                          : "—"}
                      </td>
                      <td className="py-2.5 pr-3 text-foreground">
                        {label}
                      </td>
                      <td className="py-2.5 pr-3 text-center">
                        {statusBadge}
                      </td>
                      <td className="py-2.5 pr-3 text-foreground text-xs">
                        {p.notes?.payment_method ? (
                          p.notes.payment_method === 'upi' ? 'UPI' :
                          p.notes.payment_method === 'card' ? 'Debit Card' :
                          p.notes.payment_method === 'emandate' ? 'Card e-Mandate' :
                          cap(p.notes.payment_method)
                        ) : '—'}
                      </td>
                      <td className="py-2.5 pl-3 text-right font-medium text-foreground whitespace-nowrap flex items-center justify-end gap-2">
                        {/* No online charge exists for an administrator-applied
                            change — it is invoiced outside the platform.
                            formatCurrency would floor the null to ₹0.00, which
                            reads as "this was free". */}
                        {p.billed_offline ? (
                          <span className="text-xs font-normal text-muted-foreground">
                            Billed offline
                          </span>
                        ) : (
                          formatCurrency(p.payable_now)
                        )}
                        {p.status === "failed" && (
                          <button
                            type="button"
                            onClick={() => {
                              setRetryingPaymentId(p._id);
                              setRetryConfirmOpen(true);
                            }}
                            className="text-[10px] px-2 py-1 rounded text-primary hover:bg-primary/10 font-medium"
                          >
                            Retry
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredHistory.length > HISTORY_PER_PAGE && (
            <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
              <span>
                {(historyPage - 1) * HISTORY_PER_PAGE + 1}–
                {Math.min(historyPage * HISTORY_PER_PAGE, filteredHistory.length)} of{" "}
                {filteredHistory.length}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Prev
                </button>
                <span className="font-medium text-foreground">
                  {historyPage} / {historyTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setHistoryPage((p) => Math.min(historyTotalPages, p + 1))
                  }
                  disabled={historyPage === historyTotalPages}
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted"
                >
                  Next <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Buy more seats on the current plan (mid-cycle add-on) */}
      <BuySeatsDialog
        open={manageSeatsOpen}
        onOpenChange={setManageSeatsOpen}
        initialSeats={1}
        onPurchased={async () => {
          try {
            const resp = await getUpgradeOptions();
            if (resp?.plans) setData(resp);
          } catch (e) {
            /* non-fatal: the seats were applied; just refresh failed */
          }
        }}
      />

      {/* Retry Payment Confirmation Modal */}
      {retryConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-2xl border border-border p-6 max-w-sm mx-4 shadow-lg">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Retry Payment?
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              This will retry the failed payment. You'll need to complete the payment process again.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setRetryConfirmOpen(false);
                  setRetryingPaymentId(null);
                }}
                disabled={retryLoading}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRetry}
                disabled={retryLoading}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {retryLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {retryLoading ? "Retrying…" : "Retry Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default UpgradePlan;
